import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, BookOpen, Eye, EyeOff, Image } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MediaLibraryPicker from '../components/admin/MediaLibraryPicker';

interface StudentStory {
  id: number;
  name: string;
  nickname?: string;
  age?: number;
  storyText: string;
  quote?: string;
  photoUrl?: string;
  illustrationUrl?: string;
  jobRole?: string;
  sortOrder: number;
  isActive: number;
  createdAt: string;
}

const emptyForm = {
  name: '',
  nickname: '',
  age: '',
  storyText: '',
  quote: '',
  photoUrl: '',
  illustrationUrl: '',
  jobRole: '',
  sortOrder: 0,
  isActive: 1,
};

export default function AdminStudentStories() {
  const [items, setItems] = useState<StudentStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'image' | 'illustration' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const res = await apiFetch<StudentStory[]>('/api/admin/stories');
    if (res.success && res.data) {
      setItems(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: StudentStory) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      nickname: item.nickname || '',
      age: item.age ? String(item.age) : '',
      storyText: item.storyText,
      quote: item.quote || '',
      photoUrl: item.photoUrl || '',
      illustrationUrl: item.illustrationUrl || '',
      jobRole: item.jobRole || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.storyText.trim()) {
      alert('請輸入姓名和故事內容');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      age: form.age ? Number(form.age) : null,
    };
    const url = editingId
      ? `/api/admin/stories/${editingId}`
      : '/api/admin/stories';
    const method = editingId ? 'PUT' : 'POST';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      fetchItems();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這個學生故事嗎？')) return;
    const res = await apiFetch(`/api/admin/stories/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchItems();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const toggleActive = async (item: StudentStory) => {
    const res = await apiFetch(`/api/admin/stories/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: item.isActive ? 0 : 1 }),
    });
    if (res.success) fetchItems();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-brand-600" />
          學生故事管理
        </h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          新增故事
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium">姓名</th>
                <th className="px-4 py-3 text-left font-medium">職位</th>
                <th className="px-4 py-3 text-left font-medium">故事摘要</th>
                <th className="px-4 py-3 text-center font-medium">狀態</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      {item.nickname && (
                        <div className="text-xs text-gray-500">「{item.nickname}」</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.jobRole || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-600 line-clamp-2 max-w-[280px]">{item.storyText}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(item)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                          item.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={item.isActive ? '顯示中' : '已隱藏'}
                      >
                        {item.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                          title="編輯"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="刪除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    暫無學生故事，點擊「新增故事」開始添加
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? '編輯故事' : '新增故事'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">姓名 *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="學生姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">暱稱</label>
                  <input
                    type="text"
                    value={form.nickname}
                    onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="例如：阿明"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">年齡</label>
                  <input
                    type="number"
                    value={form.age}
                    onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">職位/崗位</label>
                  <input
                    type="text"
                    value={form.jobRole}
                    onChange={(e) => setForm((f) => ({ ...f, jobRole: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="例如：廚房助理"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">故事內容 *</label>
                <textarea
                  value={form.storyText}
                  onChange={(e) => setForm((f) => ({ ...f, storyText: e.target.value }))}
                  rows={5}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="這位學生的成長故事..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">金句</label>
                <input
                  type="text"
                  value={form.quote}
                  onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="學生說過的一句印象深刻的話"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">照片 URL</label>
                  <button
                    type="button"
                    onClick={() => { setPickerTarget('image'); setPickerOpen(true); }}
                    className="text-xs flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Image className="w-3.5 h-3.5" />
                    從媒體庫選擇
                  </button>
                </div>
                <input
                  type="text"
                  value={form.photoUrl}
                  onChange={(e) => setForm((f) => ({ ...f, photoUrl: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="https://..."
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">阿占插圖 URL</label>
                  <button
                    type="button"
                    onClick={() => { setPickerTarget('illustration'); setPickerOpen(true); }}
                    className="text-xs flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Image className="w-3.5 h-3.5" />
                    從媒體庫選擇
                  </button>
                </div>
                <input
                  type="text"
                  value={form.illustrationUrl}
                  onChange={(e) => setForm((f) => ({ ...f, illustrationUrl: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={!!form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))}
                    className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    前台顯示
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">排序</label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                />
              </div>
            </div>
            <MediaLibraryPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              filterType={'image'}
              onSelect={(url) => {
                if (pickerTarget === 'image') setForm((f) => ({ ...f, photoUrl: url }));
                else if (pickerTarget === 'illustration') setForm((f) => ({ ...f, illustrationUrl: url }));
              }}
            />
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
